<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-2">Sales Performance</h1>
        <p class="grey--text text--darken-1">
          Review sales by event report and by individual packages sold.
        </p>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card class="mb-4">
      <v-card-title>
        Filters
        <v-spacer />
        <v-btn small color="primary" :loading="loading" @click="loadSalesData">
          Refresh
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-menu
              v-model="startMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.startDate"
                  label="Start Date"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="filters.startDate"
                @input="startMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" md="3">
            <v-menu
              v-model="endMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.endDate"
                  label="End Date"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="filters.endDate"
                @input="endMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.quarterKey"
              :items="quarterOptions"
              label="Quarter"
              outlined
              dense
              clearable
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.auctioneer"
              :items="auctioneerOptions"
              label="Auctioneer"
              outlined
              dense
              clearable
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="filters.salesperson"
              :items="salespersonOptions"
              label="Salesperson"
              outlined
              dense
              clearable
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- KPI Cards -->
    <v-row class="mb-4">
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Event Report Total</div>
            <div class="headline font-weight-bold">
              {{ formatCurrency(totalEventReportValue) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Package Sales</div>
            <div class="headline font-weight-bold">
              {{ formatCurrency(totalPackageSales) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Net Sale</div>
            <div class="headline font-weight-bold">
              {{ formatCurrency(totalNetSale) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Events</div>
            <div class="headline font-weight-bold">
              {{ filteredEventRows.length }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabs -->
    <v-card>
      <v-tabs v-model="tab" background-color="primary" dark>
        <v-tab>By Event</v-tab>
        <v-tab>By Package Sold</v-tab>
      </v-tabs>

      <v-card-title>
        <span v-if="tab === 0">Event Reports</span>
        <span v-else>Packages Sold</span>

        <v-spacer />

        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          dense
          clearable
        />
      </v-card-title>

      <v-tabs-items v-model="tab">
        <!-- By Event -->
        <v-tab-item>
          <v-data-table
            :headers="eventHeaders"
            :items="filteredEventRows"
            :search="search"
            :loading="loading"
            :items-per-page="25"
            sort-by="eventReportTotal"
            sort-desc
            class="elevation-1"
          >
            <template v-slot:item.eventDate="{ item }">
              {{ formatDate(item.eventDate) }}
            </template>

            <template v-slot:item.eventReportTotal="{ item }">
              {{ formatCurrency(item.eventReportTotal) }}
            </template>

            <template v-slot:item.costToOrg="{ item }">
              {{ formatCurrency(item.costToOrg) }}
            </template>

            <template v-slot:item.estimatedProfit="{ item }">
              <strong>{{ formatCurrency(item.estimatedProfit) }}</strong>
            </template>

            <template v-slot:item.isPaid="{ item }">
              <v-chip small :color="item.isPaid ? 'green' : 'orange'" dark>
                {{ item.isPaid ? "Paid" : "Open" }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- By Package Sold -->
        <v-tab-item>
          <v-data-table
            :headers="packageHeaders"
            :items="filteredPackageRows"
            :search="search"
            :loading="loading"
            :items-per-page="25"
            sort-by="netSale"
            sort-desc
            class="elevation-1"
          >
            <template v-slot:item.issueDate="{ item }">
              {{ formatDate(item.issueDate) }}
            </template>

            <template v-slot:item.price="{ item }">
              {{ formatCurrency(item.price) }}
            </template>

            <template v-slot:item.openAccount="{ item }">
              {{ formatCurrency(item.openAccount) }}
            </template>

            <template v-slot:item.commPaid="{ item }">
              {{ formatCurrency(item.commPaid) }}
            </template>

            <template v-slot:item.netSale="{ item }">
              <strong>{{ formatCurrency(item.netSale) }}</strong>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import { portalsApp } from "@/firebase";

export default {
  name: "Sales",

  data() {
    return {
      loading: false,
      tab: 0,
      search: "",
      startMenu: false,
      endMenu: false,

      filters: {
        startDate: null,
        endDate: null,
        quarterKey: null,
        auctioneer: null,
        salesperson: null,
      },
      eventRows: [],
      packageRows: [],

      eventHeaders: [
        { text: "Event #", value: "eventReportNumber" },
        { text: "Event Date", value: "eventDate" },
        { text: "Organization", value: "organizationName" },
        { text: "Event Name", value: "eventName" },
        { text: "Auctioneer", value: "auctioneerName" },
        { text: "Salesperson", value: "salesperson" },
        { text: "Winners", value: "numberOfWinners" },
        { text: "Event Total", value: "eventReportTotal" },
        { text: "Cost To Org", value: "costToOrg" },
        { text: "Est. Profit", value: "estimatedProfit" },
        { text: "Paid", value: "isPaid" },
      ],

      packageHeaders: [
        { text: "Issue Date", value: "issueDate" },
        { text: "Sponsor/Org", value: "organization" },
        { text: "Traveler", value: "traveler" },
        { text: "PUID", value: "puid" },
        { text: "Package Code", value: "packageCode" },
        { text: "Destination", value: "packageDestination" },
        { text: "Auctioneer", value: "auctioneer" },
        { text: "Salesperson", value: "salesperson" },
        { text: "Price", value: "price" },
        { text: "Open Account", value: "openAccount" },
        { text: "Commission", value: "commPaid" },
        { text: "Net Sale", value: "netSale" },
      ],
    };
  },

  computed: {
    filteredEventRows() {
      return this.applyCommonFilters(this.eventRows, "eventDate");
    },

    filteredPackageRows() {
      return this.applyCommonFilters(this.packageRows, "issueDate");
    },

    auctioneerOptions() {
      var values = [];

      this.eventRows.forEach((row) => {
        if (row.auctioneerName && values.indexOf(row.auctioneerName) === -1) {
          values.push(row.auctioneerName);
        }
      });

      this.packageRows.forEach((row) => {
        if (row.auctioneer && values.indexOf(row.auctioneer) === -1) {
          values.push(row.auctioneer);
        }
      });

      return values.sort();
    },

    salespersonOptions() {
      var values = [];

      this.eventRows.forEach((row) => {
        if (row.salesperson && values.indexOf(row.salesperson) === -1) {
          values.push(row.salesperson);
        }
      });

      this.packageRows.forEach((row) => {
        if (row.salesperson && values.indexOf(row.salesperson) === -1) {
          values.push(row.salesperson);
        }
      });

      return values.sort();
    },

    totalEventReportValue() {
      var total = 0;
      this.filteredEventRows.forEach((row) => {
        total += this.toNumber(row.eventReportTotal);
      });
      return total;
    },

    totalPackageSales() {
      var total = 0;
      this.filteredPackageRows.forEach((row) => {
        total += this.toNumber(row.price);
      });
      return total;
    },

    totalNetSale() {
      var total = 0;
      this.filteredPackageRows.forEach((row) => {
        total += this.toNumber(row.netSale);
      });
      return total;
    },
    quarterOptions() {
      var options = [];
      var currentYear = moment().year();
      var currentQuarter = moment().quarter();

      for (var year = 2016; year <= currentYear; year++) {
        var maxQuarter = year === currentYear ? currentQuarter : 4;

        for (var quarter = 1; quarter <= maxQuarter; quarter++) {
          options.push({
            text: year + " Q" + quarter,
            value: year + "-Q" + quarter,
          });
        }
      }

      return options.reverse();
    },
  },

  mounted() {
    this.loadSalesData();
  },

  methods: {
    async loadSalesData() {
      this.loading = true;

      try {
        await Promise.all([this.loadEventReports(), this.loadMpsPackages()]);
      } catch (error) {
        console.error("Error loading sales data:", error);

        if (this.$store) {
          this.$store.dispatch("setSnackbar", {
            display: true,
            color: "error",
            text: "Unable to load sales data",
            timeout: 5000,
          });
        }
      }

      this.loading = false;
    },

    async loadEventReports() {
      var snapshot = await portalsApp
        .firestore()
        .collection("eventReports")
        .get();

      var rows = [];

      snapshot.forEach((doc) => {
        var data = doc.data() || {};
        var winners = data.winners || [];
        var costToOrg = 0;

        winners.forEach((winner) => {
          costToOrg += this.toNumber(winner.costToOrg);
        });

        var eventTotal = this.toNumber(data.eventReportTotal);
        var estimatedProfit = eventTotal - costToOrg;

        rows.push({
          id: doc.id,
          eventReportNumber: data.eventReportNumber || "",
          eventDate: data.eventDate || data.dateCreated || "",
          organizationName: data.organizationName || "",
          eventName: data.eventName || "",
          auctioneerName:
            data.auctioneerName || data.auctioneerCompany || "None",
          salesperson:
            data.savSalesName === "Other"
              ? data.savSalesOtherName || "Other"
              : data.savSalesName || "Unassigned",
          numberOfWinners: data.numberOfWinners || winners.length || 0,
          eventReportTotal: eventTotal,
          costToOrg: costToOrg,
          estimatedProfit: estimatedProfit,
          isPaid: data.isPaid || false,
        });
      });

      this.eventRows = rows;
    },

    async loadMpsPackages() {
      var snapshot = await portalsApp
        .firestore()
        .collection("mps")
        .get();
      var rows = [];

      snapshot.forEach((doc) => {
        var data = doc.data() || {};

        var price = this.toNumber(data.price);
        var openAccount = this.toNumber(data.openAccount);
        var commPaid = this.toNumber(data.commPaid);

        var netSale =
          data.netSale !== undefined &&
          data.netSale !== null &&
          data.netSale !== ""
            ? this.toNumber(data.netSale)
            : price + openAccount - commPaid;

        rows.push({
          id: doc.id,
          issueDate: data.issueDate || data.dateCreated || "",
          organization:
            data.sponsorOrgUpdate || data.sponsorOrganization || "N/A",
          traveler: data.traveler || "",
          puid: data.puid || "",
          packageCode: data.packageCode || "",
          packageDestination: data.packageDestination || data.destination || "",
          auctioneer: this.getMpsAuctioneer(data),
          salesperson: data.rep || "Unassigned",
          price: price,
          openAccount: openAccount,
          commPaid: commPaid,
          netSale: netSale,
        });
      });

      this.packageRows = rows;
    },

    applyCommonFilters(rows, dateField) {
      var output = rows.slice();

      if (this.filters.auctioneer) {
        output = output.filter((row) => {
          var auctioneer = row.auctioneerName || row.auctioneer || "";
          return auctioneer === this.filters.auctioneer;
        });
      }

      if (this.filters.salesperson) {
        output = output.filter((row) => {
          return row.salesperson === this.filters.salesperson;
        });
      }

      if (this.filters.quarterKey) {
        var quarterParts = this.filters.quarterKey.split("-Q");
        var year = Number(quarterParts[0]);
        var quarter = Number(quarterParts[1]);

        var quarterStart = moment()
          .year(year)
          .quarter(quarter)
          .startOf("quarter");

        var quarterEnd = moment()
          .year(year)
          .quarter(quarter)
          .endOf("quarter");

        output = output.filter((row) => {
          var parsed = this.parseDate(row[dateField]);
          if (!parsed || !parsed.isValid()) return false;

          return parsed.isBetween(quarterStart, quarterEnd, "day", "[]");
        });

        return output;
      }

      if (this.filters.startDate) {
        output = output.filter((row) => {
          var parsed = this.parseDate(row[dateField]);
          if (!parsed || !parsed.isValid()) return false;
          return parsed.isSameOrAfter(this.filters.startDate, "day");
        });
      }

      if (this.filters.endDate) {
        output = output.filter((row) => {
          var parsed = this.parseDate(row[dateField]);
          if (!parsed || !parsed.isValid()) return false;
          return parsed.isSameOrBefore(this.filters.endDate, "day");
        });
      }

      return output;
    },

    getMpsAuctioneer(data) {
      if (data.sponsorOrgSeparate && data.sponsorOrgSeparate.auctioneerName) {
        return data.sponsorOrgSeparate.auctioneerName;
      }

      if (
        data.sponsorOrgSeparate &&
        data.sponsorOrgSeparate.auctioneerCompany
      ) {
        return data.sponsorOrgSeparate.auctioneerCompany;
      }

      return "None";
    },

    parseDate(value) {
      if (!value) return null;

      var parsed = moment(
        value,
        [
          "YYYY-MM-DD",
          "MM-DD-YYYY",
          "M-D-YYYY",
          "MM/DD/YYYY",
          "M/D/YYYY",
          moment.ISO_8601,
        ],
        true,
      );

      if (!parsed.isValid()) {
        parsed = moment(value);
      }

      return parsed;
    },

    toNumber(value) {
      if (value === undefined || value === null || value === "") return 0;

      if (typeof value === "string") {
        value = value
          .replace("$", "")
          .replace(/,/g, "")
          .trim();
      }

      var number = Number(value);

      if (isNaN(number)) return 0;

      return number;
    },

    formatCurrency(value) {
      return this.toNumber(value).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    },

    formatDate(value) {
      if (!value) return "";

      var parsed = this.parseDate(value);

      if (!parsed || !parsed.isValid()) return value;

      return parsed.format("MMM D, YYYY");
    },
  },
};
</script>
